import React from "react";
import ReactMarkdown from "react-markdown";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Grid,
  Popover,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ImageIcon from "@material-ui/icons/Image";
import ScheduleIcon from "@material-ui/icons/Schedule";
import LayersIcon from "@material-ui/icons/Layers";
import ExtensionIcon from "@material-ui/icons/Extension";

const TemplateTourDialogView = ({
  open,
  onClose,
  classes,
  t,
  selectedThemeIndex,
  setSelectedThemeIndex,
  templateData,
  currentTours,
  isLoading,
  copyingTourId,
  handleCopyTour,
  descriptionAnchorEl,
  selectedTourForDescription,
  handleOpenDescription,
  handleCloseDescription,
  errorMessage,
  requestDialogTour,
  isRequesting,
  requestSuccess,
  handleOpenRequestDialog,
  handleCloseRequestDialog,
  handleConfirmRequest,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.dialogTitle}>
          {t("template_tour_dialog.title")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {!isLoading && (
          <Box className={classes.themeFiltersContainer}>
            <Button
              size="small"
              className={`${classes.themeButton} ${selectedThemeIndex === 0
                ? classes.themeButtonActive
                : classes.themeButtonInactive
                }`}
              onClick={() => setSelectedThemeIndex(0)}
            >
              {t("template_tour_dialog.all_themes")}
            </Button>
            {templateData.map((theme, index) => (
              <Button
                key={theme.id}
                size="small"
                className={`${classes.themeButton} ${selectedThemeIndex === index + 1
                  ? classes.themeButtonActive
                  : classes.themeButtonInactive
                  }`}
                onClick={() => setSelectedThemeIndex(index + 1)}
              >
                {theme.name}
              </Button>
            ))}
          </Box>
        )}

        {errorMessage && (
          <Box className={classes.messagesContainer}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.toursGrid}>
            {currentTours.length === 0 ? (
              <Typography className={classes.emptyState}>
                {t("template_tour_dialog.no_tours")}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {currentTours.map((tour) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`${tour.id}-${tour.themeName}`}>
                    {(() => {
                      const displayZonesCount = tour.nbZones != null ? tour.nbZones : tour.zonesCount;
                      const displayEnigmasCount = tour.nbEnigmas != null ? tour.nbEnigmas : tour.enigmasCount;
                      const isOnRequest = tour.type === "on_request";
                      return (
                        <Box className={classes.tourCard}>
                          <Box className={classes.tourCardImageWrapper}>
                            {tour.imageUrl ? (
                              <img
                                src={tour.imageUrl}
                                alt={tour.title}
                                className={classes.tourCardImage}
                              />
                            ) : (
                              <Box className={classes.tourCardImagePlaceholder}>
                                <ImageIcon style={{ color: "#fff", fontSize: 40, opacity: 0.5 }} />
                              </Box>
                            )}

                            <IconButton
                              className={classes.infoButton}
                              size="small"
                              onClick={(e) => handleOpenDescription(e, tour)}
                            >
                              <InfoOutlinedIcon style={{ fontSize: 16 }} />
                            </IconButton>

                            {tour.durationMinutes && (
                              <Box className={classes.durationBadge}>
                                <ScheduleIcon style={{ fontSize: 12 }} />
                                {tour.durationMinutes} min
                              </Box>
                            )}

                            {tour.themeName && (
                              <Typography className={classes.themeBadge}>
                                {tour.themeName}
                              </Typography>
                            )}
                          </Box>

                          <Box className={classes.tourCardContent}>
                            <Typography className={classes.tourCardTitle}>
                              {tour.title}
                            </Typography>
                          </Box>

                          {(displayZonesCount != null || displayEnigmasCount != null) && (
                            <Box className={classes.tourCardStats}>
                              {displayZonesCount != null && (
                                <Box className={classes.statItem}>
                                  <LayersIcon style={{ fontSize: 14 }} />
                                  {displayZonesCount} {t("template_tour_dialog.zones")}
                                </Box>
                              )}
                              {displayEnigmasCount != null && (
                                <Box className={classes.statItem}>
                                  <ExtensionIcon style={{ fontSize: 14 }} />
                                  {displayEnigmasCount} {t("template_tour_dialog.enigmas")}
                                </Box>
                              )}
                            </Box>
                          )}

                          <Box className={classes.tourCardActions}>
                            {isOnRequest ? (
                              <Button
                                variant="contained"
                                className={classes.requestButton}
                                onClick={() => handleOpenRequestDialog(tour)}
                                size="small"
                              >
                                {t("template_tour_dialog.request_button")}
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                className={classes.copyButton}
                                onClick={() => handleCopyTour(tour.id)}
                                disabled={copyingTourId !== null}
                                size="small"
                              >
                                {copyingTourId === tour.id ? (
                                  <CircularProgress size={16} style={{ color: "white" }} />
                                ) : (
                                  t("template_tour_dialog.copy_button")
                                )}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        <Popover
          open={Boolean(descriptionAnchorEl)}
          anchorEl={descriptionAnchorEl}
          onClose={handleCloseDescription}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box className={classes.descriptionPopover}>
            {selectedTourForDescription && (
              <>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ fontWeight: 600 }}
                >
                  {selectedTourForDescription.title}
                </Typography>
                {selectedTourForDescription.description ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => (
                        <Typography variant="body2" color="textSecondary" paragraph>
                          {children}
                        </Typography>
                      ),
                      h1: ({ children }) => (
                        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                          {children}
                        </Typography>
                      ),
                      h2: ({ children }) => (
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {children}
                        </Typography>
                      ),
                      h3: ({ children }) => (
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {children}
                        </Typography>
                      ),
                      ul: ({ children }) => (
                        <ul style={{ paddingLeft: 16, margin: "4px 0" }}>{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol style={{ paddingLeft: 16, margin: "4px 0" }}>{children}</ol>
                      ),
                      li: ({ children }) => (
                        <li>
                          <Typography variant="body2" color="textSecondary" component="span">
                            {children}
                          </Typography>
                        </li>
                      ),
                      strong: ({ children }) => (
                        <strong style={{ fontWeight: 700 }}>{children}</strong>
                      ),
                      em: ({ children }) => <em>{children}</em>,
                    }}
                  >
                    {selectedTourForDescription.description.replace(/\n/g, "  \n")}
                  </ReactMarkdown>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {t("template_tour_dialog.no_description")}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Popover>
      </DialogContent>

      <Dialog
        open={Boolean(requestDialogTour)}
        onClose={!isRequesting ? handleCloseRequestDialog : undefined}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: "12px" } }}
      >
        <Box style={{ padding: "24px 24px 8px" }}>
          <Typography variant="h6" style={{ fontWeight: 700, color: "#2A3142", marginBottom: 12 }}>
            {requestDialogTour?.title}
          </Typography>
          {requestSuccess ? (
            <Typography variant="body2" style={{ color: "#4CAF50", fontWeight: 500 }}>
              {t("template_tour_dialog.request_success")}
            </Typography>
          ) : (
            <Box>
              {requestDialogTour?.moreInfoUrl && (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  {t("template_tour_dialog.request_more_info_prefix")}{" "}
                  <a
                    href={requestDialogTour.moreInfoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#FF7043", fontWeight: 600 }}
                  >
                    {t("template_tour_dialog.request_more_info_link")}
                  </a>
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                {t("template_tour_dialog.request_description")}
              </Typography>
            </Box>
          )}
        </Box>
        <DialogActions style={{ padding: "16px 24px" }}>
          {requestSuccess ? (
            <Button
              onClick={handleCloseRequestDialog}
              variant="contained"
              style={{ backgroundColor: "#4CAF50", color: "#fff", borderRadius: "20px", textTransform: "none", fontWeight: 600 }}
            >
              {t("template_tour_dialog.request_close")}
            </Button>
          ) : (
            <>
              <Button
                onClick={handleCloseRequestDialog}
                disabled={isRequesting}
                style={{ borderRadius: "20px", textTransform: "none", fontWeight: 600, color: "#555" }}
              >
                {t("template_tour_dialog.request_cancel")}
              </Button>
              <Button
                onClick={handleConfirmRequest}
                variant="contained"
                disabled={isRequesting}
                className={classes.requestButton}
                style={{ borderRadius: "20px", textTransform: "none", fontWeight: 600 }}
              >
                {isRequesting ? (
                  <CircularProgress size={16} style={{ color: "white" }} />
                ) : (
                  t("template_tour_dialog.request_confirm")
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TemplateTourDialogView;
